import type { Session } from '@supabase/supabase-js';

// Resolve the app role for client-side routing/guards.
//
// The custom_access_token_hook projects the app role into the access token's
// `app_metadata.role` claim — sourced from `auth_user_roles` — for EVERY
// session. By contrast, `session.user.app_metadata` only reflects the user's
// stored `raw_app_meta_data`, which is unset for users provisioned via an
// `auth_user_roles` row alone (e.g. admins, or admin-created dancers). Reading
// only `session.user.app_metadata.role` therefore made such users fall through
// to the `/parent` default. We prefer the token claim, with the user-object
// value as a fallback.
//
// Routing is NOT a security boundary — RLS (which reads the same token claim
// via jwt_role()) is. This only decides which dashboard the client shows.

export function roleFromToken(accessToken?: string | null): string | undefined {
  try {
    const part = accessToken?.split('.')[1];
    if (!part) return undefined;
    const b64 = part
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(part.length / 4) * 4, '=');
    const claims = JSON.parse(atob(b64)) as { app_metadata?: { role?: string } };
    return claims?.app_metadata?.role;
  } catch {
    return undefined;
  }
}

export function sessionRole(session: Session | null | undefined): string | undefined {
  if (!session) return undefined;
  return (
    roleFromToken(session.access_token) ??
    (session.user?.app_metadata as { role?: string } | undefined)?.role
  );
}

// The roles the client knows how to route. A user whose claim is missing or
// holds an unrecognized value has no dashboard and must be sent to the
// explicit "no role assigned" state rather than silently defaulted to parent.
export const KNOWN_ROLES = ['admin', 'faculty', 'dancer', 'parent'] as const;
export type KnownRole = (typeof KNOWN_ROLES)[number];

export function isKnownRole(role: string | null | undefined): role is KnownRole {
  return role != null && (KNOWN_ROLES as readonly string[]).includes(role);
}

// Map an authenticated session to its dashboard path. A recognized role goes
// to its own dashboard; anything else (claimless or unknown) goes to the
// explicit no-role state. This is the single source of truth for the silent
// "default to parent" decision that previously lived inline in route guards.
export function resolveDashboardPath(session: Session | null | undefined): string {
  const role = sessionRole(session);
  if (!isKnownRole(role)) return '/no-role';
  switch (role) {
    case 'admin':
      return '/admin';
    case 'faculty':
      return '/faculty';
    case 'dancer':
      return '/dancer';
    case 'parent':
      return '/parent';
  }
}
